import type { AssetGroup } from '@/types/asset';
import { toast } from '@music-vine/cadence/ui';
import { Slot as SlotPrimitive } from 'radix-ui';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { type DragEvent, type ReactElement, useState } from 'react';
import { useMedia } from 'react-use';
import { useBoardsContext } from './context-provider';
import { draggedAssetAtom, sidebarBoardDropEnabledAtom } from './sidebar-drag-atoms';
import { getBoardAssetTypeFromAssetGroup } from './utils/get-board-asset-type-from-asset-group';
import { getDefaultBoardName } from './utils/get-default-board-name';
const DRAG_IMAGE_CURSOR_OFFSET_PX = -12;
const DRAG_IMAGE_CLASSES = 'fixed -top-96 left-0 max-w-64 truncate rounded-full bg-gray-900 px-4 py-2 font-semibold text-sm text-white shadow-lg dark:bg-white dark:text-gray-900';

/** Props to spread on an asset's root element to make it draggable onto sidebar boards. */
export const useBoardDragSource = (asset: AssetGroup) => {
  const dropEnabled = useAtomValue(sidebarBoardDropEnabledAtom);
  // ponytail: touch drag would hijack long-press, and the sidebar isn't there on touch layouts anyway
  const hasMouse = useMedia('(pointer: fine)', false);
  const setDraggedAsset = useSetAtom(draggedAssetAtom);
  if (!(dropEnabled && hasMouse)) {
    return {};
  }
  return {
    draggable: true,
    onDragStart: (event: DragEvent) => {
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('text/plain', asset.data.name);

      // Spotify-style pill instead of the browser's translucent snapshot of the whole row.
      const dragImage = document.createElement('div');
      dragImage.className = DRAG_IMAGE_CLASSES;
      dragImage.textContent = asset.data.name;
      document.body.append(dragImage);
      event.dataTransfer.setDragImage(dragImage, DRAG_IMAGE_CURSOR_OFFSET_PX, DRAG_IMAGE_CURSOR_OFFSET_PX);
      setTimeout(() => dragImage.remove());
      setDraggedAsset(asset);
    },
    onDragEnd: () => setDraggedAsset(null)
  };
};
interface BoardDropTargetProps {
  children: ReactElement;
  /** Board to add the dropped asset to, or 'new' to create a board from it. */
  target: number | 'new';
}

/**
 * Makes its child a drop zone for dragged assets. Sets `data-drag-active` while
 * any asset is being dragged and `data-drop-over` while one is over this target.
 */
export const BoardDropTarget = ({
  children,
  target
}: BoardDropTargetProps) => {
  const [draggedAsset, setDraggedAsset] = useAtom(draggedAssetAtom);
  const [isOver, setIsOver] = useState(false);
  const {
    addAssetToBoard,
    assetExistsInBoard,
    createBoard,
    getBoard
  } = useBoardsContext();
  const dropAsset = (asset: AssetGroup) => {
    const assetId = Number(asset.data.id);
    const assetType = getBoardAssetTypeFromAssetGroup(asset);
    if (target === 'new') {
      void createBoard({
        name: getDefaultBoardName({
          asset
        }),
        asset: {
          id: assetId,
          type: assetType
        }
      });
      return;
    }
    const boardName = getBoard({
      id: target
    })?.name ?? 'board';
    if (assetExistsInBoard({
      assetId,
      assetType,
      boardId: target
    })) {
      toast.info(`Already in ${boardName}`);
      return;
    }
    addAssetToBoard({
      assetId,
      assetType,
      boardId: target
    });
    toast.success(`Added to ${boardName}`);
  };
  return <SlotPrimitive.Root data-drag-active={draggedAsset ? '' : undefined} data-drop-over={isOver ? '' : undefined} onDragLeave={(event: DragEvent) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsOver(false);
    }
  }} onDragOver={(event: DragEvent) => {
    // Only accept our own asset drags, not files or links from elsewhere.
    if (!draggedAsset) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setIsOver(true);
  }} onDrop={(event: DragEvent) => {
    event.preventDefault();
    setIsOver(false);
    if (draggedAsset) {
      dropAsset(draggedAsset);
    }
    // dragend never fires if the source unmounted mid-drag (e.g. virtualised lists)
    setDraggedAsset(null);
  }} data-sentry-element="SlotPrimitive.Root" data-sentry-component="BoardDropTarget" data-sentry-source-file="sidebar-drag.tsx">
      {children}
    </SlotPrimitive.Root>;
};